import React from 'react';
import { Box, Stack, ResponsiveValue, Center, useBreakpointValue } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { CustomImage, CustomText } from '../atoms';
import { InputWithIcon } from '../molecules/InputWithIcon';
import { JaggedBorderHeader } from '../atoms/JaggedBorderHeader';
import { JaggedBorderHeaderMobile } from '../atoms/JaggedBorderHeaderMobile';

interface BannerWithSearchBarForPlayerHubProps {
  imageSrc: string | undefined;
  altText: string;
  bannerText?: string;
  placeholderFontSize?: ResponsiveValue<string>;
  placeholderText: string;
  placeholderColor?: string;
  searchBarTextColor?: string;
  px?: ResponsiveValue<string>;
  isFullScreen?: boolean;
  bannerTextColor?: string;
  value: string;
  bgColor?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showJaggedBorder?: boolean;
  secondaryAccentColor?: string;
  placeholderFontWeight?: string;
}

export const BannerWithSearchBarForPlayerHub: React.FC<BannerWithSearchBarForPlayerHubProps> = ({
  isFullScreen = false,
  imageSrc,
  altText,
  bannerText,
  px = { base: 0, md: '1vw', lg: '15vw' },
  bannerTextColor = 'white',
  placeholderText = 'Search Fields',
  placeholderColor = 'white',
  searchBarTextColor = 'white',
  placeholderFontSize,
  value,
  onChange,
  bgColor,
  showJaggedBorder,
  secondaryAccentColor,
  placeholderFontWeight,
}) => {
  // eslint-disable-next-line no-console
  const isMobile = useBreakpointValue({ base: true, md: false });
  return (
    <Box
      position="relative"
      height={{ base: '50vh', md: '60vh' }}
      minHeight={{ base: '320px', md: '420px' }} // ✅ safety
      overflow="visible"
      px={px}
      sx={{
        '@media (orientation: landscape) and (max-height: 430px)': {
          height: '45vh',
          minHeight: '280px',
        },
      }}
    >
      {/* Background */}
      <CustomImage src={imageSrc} alt={altText} objectFit="cover" width="100%" height="100%" />
      {/* Content */}
      <Box
        position="absolute"
        bottom="8"
        right="0"
        width="100%"
        height="50%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        px={{ base: 4, md: 8, lg: 0 }}
        zIndex="2"
      >
        <Center height="100%">
          <Stack spacing={4} width="100%">
            <CustomText
              variant="h1"
              color={bannerTextColor}
              textAlign="center"
              fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
              fontWeight="700"
            >
              {bannerText}
            </CustomText>

            {/* SEARCH BAR WRAPPER (KEY FIX) */}
            <Box
              width={{ base: '90%', md: '85%', lg: '70%' }}
              mx="auto"
              height="64px"
              display="flex"
              alignItems="center"
              sx={{
                '@media (orientation: landscape) and (max-height: 430px)': {
                  height: '64px', // 🔒 DO NOT SHRINK
                },
                '@media (min-width: 768px)': {
                  height: '72px',
                },
              }}
            >
              <Box width="100%">
                <InputWithIcon
                  isFullScreen={isFullScreen}
                  onChange={onChange}
                  value={value}
                  placeholder={placeholderText}
                  placeholderColor={placeholderColor}
                  placeholderFontSize={placeholderFontSize}
                  InputIcon={SearchIcon}
                  iconSize={{ base: '20px', md: '24px', lg: '28px' }}
                  iconColor={secondaryAccentColor}
                  height="44px" // fixed input height
                  px="0"
                  searchBarTextColor={searchBarTextColor}
                  searchBarBgColor="linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.3))"
                  borderColor={secondaryAccentColor}
                  borderRadius="0"
                  placeholderFontWeight={placeholderFontWeight}
                />
              </Box>
            </Box>
          </Stack>
        </Center>
      </Box>

      {showJaggedBorder &&
        (isMobile ? (
          <JaggedBorderHeaderMobile bgColor={bgColor} />
        ) : (
          <JaggedBorderHeader bgColor={bgColor} />
        ))}
    </Box>
  );
};
